/**
 * 쿼리 키 팩토리 정의기 — **키가 도메인 루트 밖으로 새는 것을 타입으로 막는다.**
 *
 * ## 왜 필요한가
 *
 * `queryKey` 의 타입은 `readonly unknown[]` 이다. 그래서 `['attendance', …]` 를 적든
 * `['attendances', …]` 를 적든 컴파일이 통과하고, 무효화가 prefix 매칭에 **조용히** 실패한다.
 * 이 레포에서 이미 다섯 번 터졌다:
 *
 * - 검토 대기 배지 2회 · 도전자 수 배지 1회 — 키가 `['quests']` 접두사 **밖**에서 태어남
 * - `['workLog']` vs `['workLogs']` (PR #1536) · `['attendance']` vs `['attendances']` (PR #1538)
 *   — 같은 도메인의 루트 철자가 갈림
 *
 * 다섯 건 모두 "루트를 손으로 적었다" 가 원인이다. 이 팩토리는 루트를 **첫 인자로 한 번만**
 * 받고, 멤버가 만드는 모든 키가 그 루트로 시작하도록 타입으로 강제한다. 철자가 갈릴 자리도,
 * 접두사 밖으로 나갈 자리도 없앤다.
 *
 * ## 쓰는 법
 *
 * ```ts
 * export const attendanceKeys = defineQueryKeys('attendances', (all) => ({
 *   calendar: (month: string | undefined) => [...all, 'calendar', month] as const,
 * }));
 * ```
 *
 * `all` 은 팩토리가 만들어 콜백에 넘긴다(`['attendances']`). 멤버는 그걸 스프레드해 키를
 * 조립하므로 **루트 문자열은 파일에 한 번만 등장한다.**
 *
 * `as const` 는 문법상 필수가 아니다 — 제약이 문맥 타입으로 배열 리터럴을 튜플로 좁혀 주므로
 * 없어도 원소 타입까지 그대로 추론된다(차이는 `readonly` 하나뿐). 그래도 기존 9개 팩토리의
 * 표기를 따라 붙여 둔다.
 *
 * 배경과 설계 경위: `docs/refactor/query-key-invalidation-contract.md` §4-3.
 *
 * ## 이 팩토리가 하지 않는 것
 *
 * **무효화 쪽은 지키지 않는다.** 뮤테이션이 `invalidateQueries({ queryKey: ['quests'] })` 라고
 * 손으로 적는 것은 여전히 통과한다. 그건 같은 문서 §4-2 의 계약 인터페이스가 맡을 몫이다.
 */

/** 도메인 루트로 시작하는 쿼리 키. 두 번째 칸부터는 무엇이 와도 좋다. */
type RootedKey<TRoot extends string> = readonly [TRoot, ...unknown[]];

/**
 * 팩토리 멤버 — 셋 중 하나다.
 *
 * 1. 정적 키 튜플 — 무효화 전용 중간 접두사(`questKeys.listAll` 같은 것)
 * 2. 키를 만드는 함수 — 조회 키 대부분
 * 3. 1·2 의 중첩 묶음 — `questKeys.acceptanceSummaries` 처럼 `all` + `batch` 를 짝지은 것
 *
 * `(...args: never[])` 는 **"인자 목록이 무엇이든 받는다"** 는 뜻이다. `never` 는 모든 타입에
 * 할당 가능하므로 파라미터 반공변성 아래서 어떤 시그니처의 함수든 이 제약을 통과한다.
 * `unknown[]` 으로 바꾸면 `strictFunctionTypes` 아래서 `(id: string) => …` 가 거부된다.
 */
type KeyMember<TRoot extends string> =
  | RootedKey<TRoot>
  | ((...args: never[]) => RootedKey<TRoot>)
  | { readonly [name: string]: KeyMember<TRoot> };

type KeyMap<TRoot extends string> = {
  readonly [name: string]: KeyMember<TRoot>;
};

/**
 * 도메인 루트 하나에 묶인 쿼리 키 팩토리를 만든다.
 *
 * `all` 은 팩토리가 소유한다 — 콜백이 같은 이름을 반환할 수 없도록 타입(`all?: never`)으로
 * 막고, 런타임에서도 콜백 결과 **뒤에** 얹어 루트가 이기게 한다. 광역 무효화가 보는 키가
 * 도메인마다 다른 규칙으로 만들어지면 이 팩토리를 쓰는 의미가 없다.
 *
 * 파라미터 이름이 `root` 인 것은 의도적이다 — 이 레포에는 `scope: 'TEAM' | 'GLOBAL'` 이
 * 실재하고 하필 `questKeys.acceptanceSummaries.batch(scope, ids)` 의 첫 인자가 그것이라,
 * `scope` 를 쓰면 두 개념이 한 파일에서 충돌한다.
 */
export function defineQueryKeys<
  const TRoot extends string,
  const TMap extends KeyMap<TRoot> & { readonly all?: never },
>(root: TRoot, build: (all: readonly [TRoot]) => TMap): { readonly all: readonly [TRoot] } & TMap {
  const all = [root] as const;

  return { ...build(all), all };
}
